import { Badge } from '@journ/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@journ/ui/card'
import { useIntlayer } from 'react-intlayer'
import type { Customer } from '../domain/customer'
import type { CustomerEnrichmentStatus } from '../domain/customer-enrichment'

type Props = {
	customer: Customer
}

const statusVariant: Record<CustomerEnrichmentStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
	pending: 'outline',
	processing: 'secondary',
	completed: 'default',
	failed: 'destructive',
}

type FieldProps = {
	label: string
	value: string | number | null
}

const Field = ({ label, value }: FieldProps) => {
	if (value === null || value === '') return null
	return (
		<div className='space-y-1'>
			<div className='text-[11px] uppercase tracking-wide text-878787'>{label}</div>
			<div className='text-sm'>{value}</div>
		</div>
	)
}

const SocialLink = ({ href, label }: { href: string | null; label: string }) => {
	if (!href) return null
	return (
		<a href={href} target='_blank' rel='noreferrer' className='text-sm text-primary hover:underline'>
			{label}
		</a>
	)
}

const CustomerEnrichmentSection = ({ customer }: Props) => {
	const content = useIntlayer('customers-page')
	const c = content.enrichment
	const status = customer.enrichmentStatus
	const statusLabel = status ? c[status] : c.notStarted
	const hasData = Boolean(
		customer.description ||
		customer.industry ||
		customer.companyType ||
		customer.employeeCount ||
		customer.headquartersLocation ||
		customer.linkedinUrl ||
		customer.twitterUrl ||
		customer.instagramUrl ||
		customer.facebookUrl,
	)

	return (
		<Card>
			<CardHeader>
				<div className='flex items-start justify-between gap-4'>
					<div>
						<CardTitle>{c.title}</CardTitle>
						<CardDescription>{c.description}</CardDescription>
					</div>
					<Badge variant={status ? statusVariant[status] : 'outline'}>{statusLabel}</Badge>
				</div>
			</CardHeader>
			<CardContent>
				{hasData ? (
					<div className='space-y-5'>
						<Field label={c.descriptionField.value} value={customer.description} />
						<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
							<Field label={c.industry.value} value={customer.industry} />
							<Field label={c.companyType.value} value={customer.companyType} />
							<Field label={c.employeeCount.value} value={customer.employeeCount} />
							<Field label={c.foundedYear.value} value={customer.foundedYear} />
							<Field label={c.estimatedRevenue.value} value={customer.estimatedRevenue} />
							<Field label={c.fundingStage.value} value={customer.fundingStage} />
							<Field label={c.totalFunding.value} value={customer.totalFunding} />
							<Field label={c.headquartersLocation.value} value={customer.headquartersLocation} />
							<Field label={c.timezone.value} value={customer.timezone} />
							<Field label={c.primaryLanguage.value} value={customer.primaryLanguage} />
							<Field label={c.financeContact.value} value={customer.financeContact} />
							<Field label={c.financeContactEmail.value} value={customer.financeContactEmail} />
						</div>
						<div className='flex flex-wrap gap-3'>
							<SocialLink href={customer.linkedinUrl} label='LinkedIn' />
							<SocialLink href={customer.twitterUrl} label='X' />
							<SocialLink href={customer.instagramUrl} label='Instagram' />
							<SocialLink href={customer.facebookUrl} label='Facebook' />
						</div>
					</div>
				) : (
					<p className='text-sm text-606060'>{c.empty}</p>
				)}
			</CardContent>
		</Card>
	)
}

export { CustomerEnrichmentSection }
